/*
eWorldES Proprietary Software
 Copyright (C) 2025 eWorldES. All rights reserved.

 This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
 You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
 Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

 License Agreement:
 By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
 For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

 This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
 merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
 arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

 For inquiries or requests regarding this software, please contact eWorldES at:
 Email: info@eworldes.com
 Website: https://eworldes.com/
*/
import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTheme, useMediaQuery, Divider } from "@mui/material";
import { FormProps } from "../../types/InputProps";
import { Stack } from "../Stack";
import Stepper from "../Stepper/Stepper";
import StepperStep from "../Stepper/StepperStep";
import StepperStepLabel from "../Stepper/StepperStepLabel";
import { Tab, Tabs } from "../Tabs";
import { Typography } from "../Typography";
import { FormFooterActions } from "./FormFooterActions";
import FormInputFields from "./FormInputFields";
import { getAllFields, getInputs } from "./getInput";
import StepperHeader from "../Stepper/StepperHeader";

const Form = (props: FormProps) => {
  const {
    initialValues = {},
    onSubmit,
    submitText,
    formView = "default",
    inputs,
    actions,
    stickyHeader = false,
    stickyFooter = false,
    isDisabled = false
  } = props;

  /* ────────────────────────────────
     mobile‑vs‑desktop breakpoint
  ──────────────────────────────────*/
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* ────────────────────────────────
     step / form state
  ──────────────────────────────────*/
  const [activeFrom, setActiveForm] = useState<number>(props.jumpTo ?? 0);
  const [hasJumped, setHasJumped] = useState(false);

  useEffect(() => {
    if (!hasJumped && typeof props.jumpTo === "number") {
      setActiveForm(props.jumpTo);
      setHasJumped(true);
    }
  }, [props.jumpTo, hasJumped]);

  const fields =
    formView === "default" ? getAllFields(inputs) : inputs[activeFrom].fields;
  const currentFormFields = getInputs<any>(fields, initialValues);
  /* action buttons ---------------------------------------------- */
  const nextAction = (actions ?? []).find((a) => a.actiontype === "next");
  const nextButtonProps =
    formView === "stepper" && nextAction
      ? {
          ...nextAction,
          disabled: nextAction.userDisabled ?? (activeFrom === inputs.length - 1)
        }
      : undefined;
  const prevButtonProps =
    formView === "stepper" &&
    (actions ?? []).find((a) => a.actiontype === "previous");

  const otherButtons = (actions ?? []).filter(
    (a) => !["next", "previous"].includes(a.actiontype),
  );

  /* react‑hook‑form --------------------------------------------- */
  const isSubmitOnNext = inputs[activeFrom].saveOnNext;
  const formMethods = useForm({
    resolver: yupResolver(currentFormFields.validationSchema),
    defaultValues: { ...(initialValues as any) },
    reValidateMode: "onChange",
    mode: "all",
  });

  const disabledSteps = Array.isArray(props.disableSteps)
    ? props.disableSteps.filter((step) => step !== 0)
    : [];
  const isStepDisabled = (index: number) => disabledSteps.includes(index);

  const findNextStep = (idx: number) => {
    let next = idx + 1;
    while (isStepDisabled(next)) next++;
    return next < inputs.length ? next : idx;
  };

  const findPreviousStep = (idx: number) => {
    let prev = idx - 1;
    while (isStepDisabled(prev)) prev--;
    return prev >= 0 ? prev : idx;
  };

  /* navigation handlers ----------------------------------------- */
  const onNextForm = async (maybeData: any) => {
    const data =
      isSubmitOnNext && maybeData ? maybeData : formMethods.getValues();

    const isFormValid = await currentFormFields.validationSchema
      .validate(formMethods.getValues(), { abortEarly: false })
      .catch(() => false);

    if (!isFormValid) {
      formMethods.trigger();
      return;
    }

    if (isSubmitOnNext) {
      try {
        const response = await onSubmit(data);
        if (response?.data != null) {
          setActiveForm(activeFrom + 1);
          nextButtonProps &&
            nextButtonProps.handler &&
            nextButtonProps.handler();
        } else {
          formMethods.setError("root", {
            type: "manual",
            message: "Something went wrong during submission.",
          });
        }
      } catch (error: any) {
        formMethods.setError("root", {
          type: "manual",
          message: error?.message || "Unexpected error occurred.",
        });
      }
    } else {
      const nextIndex = findNextStep(activeFrom);
      setActiveForm(nextIndex);
      nextButtonProps?.handler?.(data);
      return data;
    }
  };

  const onPreviousForm = () => {
    prevButtonProps && prevButtonProps.handler && prevButtonProps.handler();
    const prevIndex = findPreviousStep(activeFrom);
    setActiveForm(prevIndex);
  };

  /* flags for footer actions ------------------------------------ */
  const hideActionButtons = formView === "default";
  const isLastForm = activeFrom === inputs.length - 1;
  const showSubmit = formView === "stepper" ? isLastForm : true;

  /* ────────────────────────────────
     JSX
  ──────────────────────────────────*/
  return (
    <Box sx={{ flexGrow: 1 }}>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))}>
          {/* Sticky header (tabs or stepper) */}
          {stickyHeader && (formView === "tabs" || formView === "stepper") && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                background:
                  theme.palette.mode === "light" ? "#FAFAFA" : "#303030",
                zIndex: 10,
                p: 2,
              }}
            >
              {/* Tabs */}
              {formView === "tabs" && (
                <Tabs value={activeFrom} onChange={(_, v) => setActiveForm(v)}>
                  {inputs.map((f, i) => (
                    <Tab label={f.title} key={`${f.title}-${i}`} />
                  ))}
                </Tabs>
              )}

              {/* Stepper / StepperHeader */}
              {formView === "stepper" &&
                (isMobile ? (
                  <StepperHeader
                    stepIndex={activeFrom}
                    steps={inputs.map((f) => f.title)}
                    onBack={onPreviousForm}
                    disabledSteps={props.disableSteps ?? []}
                    onNext={() =>
                      isSubmitOnNext
                        ? formMethods.handleSubmit(onNextForm)()
                        : onNextForm(undefined)
                    }
                    onJumpTo={(idx) => setActiveForm(idx)}
                  />
                ) : (
                  <Stepper
                    activeStep={activeFrom}
                    sx={{ flexDirection: { xs: "column", sm: "row" } }}
                  >
                    {inputs.map((f, i) => (
                      <StepperStep key={`${f.title}-${i}`}>
                        <StepperStepLabel>{f.title}</StepperStepLabel>
                      </StepperStep>
                    ))}
                  </Stepper>
                ))}
            </Box>
          )}

          {/* Form body ------------------------------------------------ */}
          <Stack direction={formView === "row" ? "row" : "column"} spacing={2}>
            {/* default / row views render every section at once */}
            {formView === "default" || formView === "row" ? (
              inputs.map((f, i) => (
                <Box key={`${f.title}-${i}`}>
                  <Typography variant="subtitle1" py={2} fontWeight={600}>
                    {f.title}
                  </Typography>
                  <FormInputFields
                    fields={f.fields}
                    leftPanelCol={f.leftPanelCol}
                    rightPanelCol={f.rightPanelCol}
                    showDivider={f.showDivider}
                    dividerColor={f.dividerColor}
                  />
                </Box>
              ))
            ) : (
              /* tabs / stepper show current section only */
              <Box>
                {/* tabs header (non‑sticky) */}
                {!stickyHeader && formView === "tabs" && (
                  <Tabs
                    value={activeFrom}
                    onChange={(_, v) => setActiveForm(v)}
                  >
                    {inputs.map((f, i) => (
                      <Tab label={f.title} key={`${f.title}-${i}`} />
                    ))}
                  </Tabs>
                )}

                {/* stepper / stepperHeader (non‑sticky) */}
                {!stickyHeader &&
                  formView === "stepper" &&
                  (isMobile ? (
                    <StepperHeader
                      stepIndex={activeFrom}
                      steps={inputs.map((f) => f.title)}
                      onBack={onPreviousForm}
                      onNext={() =>
                        isSubmitOnNext
                          ? formMethods.handleSubmit(onNextForm)()
                          : onNextForm(undefined)
                      }
                      onJumpTo={(idx) => setActiveForm(idx)}
                    />
                  ) : (
                    <Stepper
                      activeStep={activeFrom}
                      sx={{
                        flexDirection: { xs: "row", sm: "row" },
                        alignItems: "center",
                        justifyContent: "flex-start",
                        "& .MuiStepLabel-root": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                        },
                        "& .MuiStepLabel-label": {
                          textAlign: "center",
                          maxWidth: "150px",
                          wordWrap: "break-word",
                        },
                        "& .MuiStepIcon-root": {
                          fontSize: "1.5rem",
                          marginBottom: "8px",
                        },
                        "& .MuiStepConnector-line": {
                          borderTopWidth: "2px",
                          borderColor: "currentColor",
                          margin: 0, // reset any incorrect offsets
                        },
                        "& .MuiStep-root": {
                          padding: "0 16px",
                        },
                      }}
                    >
                      {inputs.map((f, i) => (
                        <StepperStep key={`${f.title}-${i}`}>
                          <StepperStepLabel>{f.title}</StepperStepLabel>
                        </StepperStep>
                      ))}
                    </Stepper>
                  ))}

                {/* current step’s fields */}
                <FormInputFields
                  fields={inputs[activeFrom].fields || []}
                  leftPanelCol={inputs[activeFrom].leftPanelCol}
                  rightPanelCol={inputs[activeFrom].rightPanelCol}
                  showDivider={inputs[activeFrom].showDivider}
                  dividerColor={inputs[activeFrom].dividerColor}
                />
              </Box>
            )}
          </Stack>

          {/* Footer actions ------------------------------------------- */}
          {formView === "stepper" ? (
            <Box>
              {!inputs[activeFrom].rightPanelCol && <Divider sx={{ mt: 3 }} />}
              <Box display={"flex"} flexDirection={{ xs: "column", md: "row" }}>
                <Box flex={inputs[activeFrom].leftPanelCol}></Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    display: { xs: "none", md: "block" },
                    borderLeft: "1px solid",
                    borderColor:
                      inputs[activeFrom].dividerColor || "transparent",
                    mx: (inputs[activeFrom].leftPanelCol ?? 0) > 0 ? 2 : 0,
                  }}
                />
                
                <Box flex={inputs[activeFrom].rightPanelCol}>
                  <FormFooterActions
                    activeFrom={activeFrom}
                    formList={inputs}
                    formMethods={formMethods}
                    hideActionButtons={hideActionButtons}
                    prevButtonProps={prevButtonProps}
                    nextButtonProps={nextButtonProps}
                    onPreviousForm={onPreviousForm}
                    onNextForm={
                      isSubmitOnNext
                        ? formMethods.handleSubmit(onNextForm)
                        : onNextForm
                    }
                    onSubmit={showSubmit && onSubmit}
                    submitText={submitText}
                    isDisabled={isDisabled}
                    otherButtons={otherButtons}
                  />
                </Box>
              </Box>
            </Box>
          ) : stickyFooter ? (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                background:
                  theme.palette.mode === "light" ? "#FAFAFA" : "#303030",
                zIndex: 10,
                pt: 3,
                pb: 1,
              }}
            >
              <Divider />
              <FormFooterActions
                activeFrom={activeFrom}
                formList={inputs}
                formMethods={formMethods}
                hideActionButtons={hideActionButtons}
                prevButtonProps={prevButtonProps}
                nextButtonProps={nextButtonProps}
                onPreviousForm={onPreviousForm}
                onNextForm={
                  isSubmitOnNext
                    ? formMethods.handleSubmit(onNextForm)
                    : onNextForm
                }
                onSubmit={showSubmit && onSubmit}
                submitText={submitText}
                otherButtons={otherButtons}
              />
            </Box>
          ) : (
            <>
              <Divider sx={{ mt: 3 }} />
              <FormFooterActions
                activeFrom={activeFrom}
                formList={inputs}
                formMethods={formMethods}
                hideActionButtons={hideActionButtons}
                prevButtonProps={prevButtonProps}
                nextButtonProps={nextButtonProps}
                onPreviousForm={onPreviousForm}
                onNextForm={
                  isSubmitOnNext
                    ? formMethods.handleSubmit(onNextForm)
                    : onNextForm
                }
                onSubmit={showSubmit && onSubmit}
                submitText={submitText}
                otherButtons={otherButtons}
              />
            </>
          )}
        </form>
      </FormProvider>
    </Box>
  );
};

export default Form;
